import { Upload, message, Button } from 'antd'
import { Input, TextArea, Select, Radio, RadioGroup, Checkbox, PhoneNumberInput } from '@/components/ui'
import { UploadOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd'
import { api } from '@/lib/apiClient'

interface FieldOption {
  label: string
  value: string
}

interface SessionOption {
  id: number
  agenda_session_id: number
  agenda_session: {
    id: number
    name: string
    start_time: string
    end_time: string
    venue?: string
  }
  capacity?: number
  position: number
}

interface RegistrationField {
  id: number
  identifier: string
  label: string
  kind: 'short_text' | 'long_text' | 'single_choice' | 'multiple_choice' |
        'dropdown' | 'file_upload' | 'consent_text' | 'session_choice' | 'phone_number'
  required: boolean
  help_text?: string
  options: FieldOption[]
  session_options?: SessionOption[]
  configuration?: {
    max_length?: number
    content_types?: string[]
    max_size_mb?: number
    consent_statement?: string
    pinned_country_codes?: string[]
  }
}

interface DynamicFormFieldProps {
  field: RegistrationField
  value: any
  onChange: (identifier: string, value: any) => void
  eventSlug?: string
}

export default function DynamicFormField({ field, value, onChange, eventSlug }: DynamicFormFieldProps) {
  const handleChange = (newValue: any) => {
    onChange(field.identifier, newValue)
  }

  const hasOptions = field.options && field.options.length > 0

  const renderField = () => {
    switch (field.kind) {
      case 'short_text':
        return (
          <Input
            placeholder={`Enter ${field.label.toLowerCase()}`}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            size="small"
          />
        )

      case 'phone_number':
        return (
          <PhoneNumberInput
            value={value || ''}
            onChange={handleChange}
            pinnedCountryCodes={field.configuration?.pinned_country_codes}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        )

      case 'long_text':
        return (
          <TextArea
            placeholder={`Enter ${field.label.toLowerCase()}`}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            rows={4}
            size="small"
          />
        )

      case 'dropdown':
        if (!hasOptions) {
          return (
            <Select
              placeholder="No options available"
              disabled
              value={undefined}
              size="small"
            />
          )
        }
        return (
          <Select
            placeholder={`Select ${field.label.toLowerCase()}`}
            value={value || undefined}
            onChange={handleChange}
            showSearch
            allowClear
            options={field.options}
            size="small"
          />
        )

      case 'single_choice':
        if (!hasOptions) {
          return (
            <div className="text-sm text-gray-400 py-2">
              No options available
            </div>
          )
        }
        return (
          <RadioGroup
            value={value || undefined}
            onChange={(e) => handleChange(e.target.value)}
          >
            {field.options.map((option) => (
              <Radio key={option.value} value={option.value} size="small">
                <span className="whitespace-pre-line">{option.label}</span>
              </Radio>
            ))}
          </RadioGroup>
        )

      case 'multiple_choice':
        if (!hasOptions) {
          return (
            <div className="text-sm text-gray-400 py-2">
              No options available
            </div>
          )
        }
        return (
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {field.options.map((option) => (
              <Checkbox
                key={option.value}
                checked={(value || []).includes(option.value)}
                onChange={(e) => {
                  const currentValues = value || []
                  if (e.target.checked) {
                    handleChange([...currentValues, option.value])
                  } else {
                    handleChange(currentValues.filter((v: string) => v !== option.value))
                  }
                }}
                size="small"
              >
                <span className="whitespace-pre-line">{option.label}</span>
              </Checkbox>
            ))}
          </div>
        )

      case 'consent_text': {
        const consentStatement =
          field.configuration?.consent_statement || field.help_text || field.label
        const checked = value === true || value === 'true'

        return (
          <div className="flex items-start gap-2">
            <Checkbox
              checked={checked}
              onChange={(e) => handleChange(e.target.checked)}
              size="small"
            />
            <div
              className="cursor-pointer text-sm text-gray-700 leading-6 whitespace-pre-wrap [&_a]:text-blue-600 [&_a]:underline"
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('a')) return

                handleChange(!checked)
              }}
              dangerouslySetInnerHTML={{ __html: consentStatement }}
            />
          </div>
        )
      }

      case 'session_choice':
        if (!field.session_options || field.session_options.length === 0) {
          return (
            <div className="text-sm text-gray-400 py-2">
              No sessions available
            </div>
          )
        }
        return (
          <RadioGroup
            value={value || undefined}
            onChange={(e) => handleChange(e.target.value)}
          >
            <div className="mt-1">
              {field.session_options.map((option) => (
                <Radio key={option.id} value={option.agenda_session_id} size="small">
                  <div className="mb-2">
                    <div className="font-medium">{option.agenda_session.name}</div>
                    <div className="text-xs text-gray-500">
                      {option.agenda_session.start_time} - {option.agenda_session.end_time}
                      {option.agenda_session.venue && ` • ${option.agenda_session.venue}`}
                      {option.capacity && ` • Capacity: ${option.capacity}`}
                    </div>
                  </div>
                </Radio>
              ))}
            </div>
          </RadioGroup>
        )

      case 'file_upload': {
        const fileList: UploadFile[] = Array.isArray(value) ? value : value?.fileList || []
        const contentTypes = field.configuration?.content_types
        const maxSizeMb = field.configuration?.max_size_mb || 10

        const uploadProps = {
          maxCount: 1,
          accept: contentTypes?.join(','),
          fileList,
          customRequest: async (options: any) => {
            const { file, onSuccess, onError } = options
            if (!eventSlug) {
              message.error('Missing event slug for upload')
              onError?.('missing event slug')
              return
            }

            const maxBytes = maxSizeMb * 1024 * 1024
            if (file.size > maxBytes) {
              const msg = `File too large. Max size: ${maxSizeMb}MB`
              message.error(msg)
              onError?.(msg)
              return
            }

            // type validation
            if (contentTypes && contentTypes.length > 0) {
              const isValidType = contentTypes.some((type) => {
                if (type.endsWith('/*')) {
                  return file.type.startsWith(type.replace('/*', '/'))
                }
                return file.type === type
              })
              if (!isValidType) {
                const msg = `Invalid file type. Allowed: ${contentTypes.join(', ')}`
                message.error(msg)
                onError?.(msg)
                return
              }
            }

            const formData = new FormData()
            formData.append('file', file as File)
            formData.append('field_identifier', field.identifier)
            formData.append('event_slug', eventSlug)

            try {
              const resp = await api.post<{
                data: { signed_id: string; blob_id: number; filename: string; byte_size: number; content_type: string }
              }>(`/private/v1/attendee/event/${eventSlug}/uploads`, formData)

              const { signed_id, blob_id, filename } = resp.data
              const newFile: UploadFile = {
                uid: blob_id.toString(),
                name: filename,
                status: 'done',
                url: undefined,
              }
              handleChange({
                blob_id,
                signed_id,
                fileList: [newFile]
              })
              onSuccess?.(resp, file)
            } catch (err: any) {
              const msg = err?.message || 'Upload failed'
              message.error(msg)
              onError?.(msg)
            }
          },
          onRemove: () => {
            handleChange(null)
          }
        } as any

        return (
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} size="small">Select File</Button>
          </Upload>
        )
      }

      default:
        return (
          <Input
            placeholder={`Enter ${field.label.toLowerCase()}`}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            size="small"
          />
        )
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {renderField()}
      {field.help_text && field.kind !== 'consent_text' && (
        <div className="text-xs text-gray-500 mt-1">{field.help_text}</div>
      )}
    </div>
  )
}
